import { css, type CSSResult } from '@design.estate/dees-element';

/**
 * Shared visual vocabulary for harness components: card chrome, status
 * pills, and monospace output blocks. Tokens only — both themes flip
 * automatically.
 */
export const harnessCardStyles: CSSResult = css`
  .harness-card {
    position: relative;
    border: 1px solid var(--dees-color-border-subtle);
    border-radius: var(--dees-radius-xl);
    corner-shape: var(--dees-corner-shape);
    background: var(--dees-color-bg-secondary);
    overflow: hidden;
  }
`;

/** Compact actions share the settings-window control metric. */
export const harnessControlStyles: CSSResult = css`
  dees-button::part(button) {
    height: var(--dees-control-height-md);
    min-height: var(--dees-control-height-md);
    border-radius: var(--dees-radius-md);
    font-size: 12px;
  }
  button:focus-visible, input:focus-visible, textarea:focus-visible {
    outline: 2px solid var(--dees-color-focus-ring);
    outline-offset: 2px;
  }
`;

/** Motion stays on the small status indicator, not on the card surface. */
export const harnessActivityStyles: CSSResult = css`
  @keyframes harness-activity-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .45; }
  }
  .harnessActivity { animation: harness-activity-pulse 1.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .harnessActivity { animation: none; }
  }
`;

export const harnessPillStyles: CSSResult = css`
  .harness-pill {
    flex: 0 0 auto;
    padding: 2px 8px;
    border-radius: var(--dees-radius-full);
    background: var(--dees-color-badge-default-bg);
    color: var(--dees-color-badge-default-fg);
    font-size: 11px;
    line-height: 16px;
    font-weight: 500;
    text-transform: capitalize;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .harness-pill.tone-running,
  .harness-pill.tone-pending {
    background: var(--dees-color-fill-secondary);
    color: var(--dees-color-accent-primary);
  }
  .harness-pill.tone-success {
    background: var(--dees-color-badge-success-bg);
    color: var(--dees-color-badge-success-fg);
  }
  .harness-pill.tone-warning {
    background: var(--dees-color-badge-warning-bg);
    color: var(--dees-color-badge-warning-fg);
  }
  .harness-pill.tone-error {
    background: var(--dees-color-badge-error-bg);
    color: var(--dees-color-badge-error-fg);
  }
`;

export const harnessMonoStyles: CSSResult = css`
  .harness-mono {
    margin: 0;
    padding: var(--dees-spacing-sm) var(--dees-spacing-md);
    border: 1px solid var(--dees-color-border-subtle);
    border-radius: var(--dees-radius-lg);
    corner-shape: var(--dees-corner-shape);
    background: var(--dees-color-code-block-bg);
    color: var(--dees-color-text-primary);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    overflow: auto;
    font-family: var(--dees-font-family-mono);
    font-size: var(--dees-font-control-size-sm, 12px);
    line-height: 1.5;
  }
  .harness-mono.tone-error {
    color: var(--dees-color-text-error);
  }
  .harness-mono-expand {
    margin-top: var(--dees-spacing-xs);
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--dees-color-link);
    cursor: pointer;
    font-size: var(--dees-font-control-size-sm, 12px);
  }
  .harness-mono-expand:hover {
    color: var(--dees-color-link-hover);
    text-decoration: underline;
  }
`;

/**
 * Panel chrome of the harness sidebars: a full-height column of collapsible
 * sections, each a heading button (chevron, optional icon, label, right-aligned
 * summary) above the scrolling body of the expanded section. Shared by
 * `dees-harness-sidebar` and `dees-harness-session-sidebar` so a host-composed
 * panel and the session panel are the same surface. It expects the
 * `.sidebar` > `.section` > `.sectionHeading` + `.sectionContent` structure and
 * styles the element itself, so it belongs to those two elements only.
 */
export const harnessSidebarStyles: CSSResult = css`
  :host {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    height: 100%;
    font-family: var(--dees-font-family);
    color: var(--dees-color-text-primary);
  }

  .sidebar {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .section {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    border-bottom: 1px solid var(--dees-color-border-subtle);
  }

  .section.expanded {
    flex: 1 1 0;
  }

  .section:last-child {
    border-bottom: 0;
  }

  .sectionHeading {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: var(--dees-spacing-xs);
    width: 100%;
    min-height: 36px;
    margin: 0;
    padding: 6px 12px 6px 8px;
    border: 0;
    background: var(--dees-color-bg-canvas);
    color: var(--dees-color-text-primary);
    font-family: inherit;
    font-size: var(--dees-font-control-size-sm, 12px);
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }

  .sectionHeading:hover {
    background: var(--dees-color-badge-default-bg);
  }

  .sectionHeading:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--dees-color-accent-primary);
    outline-offset: -2px;
  }

  .sectionHeading dees-icon {
    flex: 0 0 auto;
    color: var(--dees-color-text-secondary);
  }

  .sectionHeadingText {
    min-width: 0;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sectionSummary {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: auto;
    color: var(--dees-color-text-muted);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }

  .sectionContent {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: var(--dees-spacing-sm) var(--dees-spacing-md);
    scrollbar-width: thin;
    scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
  }

  .sectionContent[hidden] {
    display: none;
  }
`;
